"use client";

import { Button } from "@neuralpilot/shared-ui";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@neuralpilot/shared-ui";
import { Input } from "@neuralpilot/shared-ui";
import { cn } from "@neuralpilot/shared-ui";
import { loginSchema, restPasswordSchema, TLogin, TPassword } from "@/schemas/auth";
import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { authServices } from "@/api/auth";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import storage from "@/lib/storage";
import { useRouter } from "next/navigation";

import { encryptPasswordForLogin } from "@/lib/password-encryption";
import { toast } from "sonner";
import { useState } from "react";

export function LoginForm({
  className,
  ...props
}: React.ComponentProps<"form">) {
  const router = useRouter();
  const [showForgetPassword, setShowForgetPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const form = useForm<TLogin>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const resetForm = useForm<TPassword>({
    resolver: zodResolver(restPasswordSchema),
    defaultValues: {
      email: ""
    },
  });

   const requestMailMutation = useMutation({
    mutationFn: authServices.mutations.requestPasswordReset,
    onSuccess: response => {

    },
  });

  const loginMutation = useMutation({
    mutationFn: authServices.mutations.login,
    onSuccess: response => {
      if (response?.statusCode === 200) {
        storage.setItem("token", response.data.accessToken);
        storage.setItem("userId", response.data.userId);
        storage.setItem("user", {...response.data.user, role :response.data.roleType});
        // Use window.location for full page reload to ensure token is available
        window.location.href = "/fleet-overview";
      }
    },
  });


  async function onSubmit(values: TLogin) {
    try {
      const result = await encryptPasswordForLogin(values.password);
      if (!result) {
        throw new Error("encryption_unavailable");
      }
      const payload: TLogin = {
        email: values.email,
        //password: result.encryptedBase64,
        password: values.password,
      };
      loginMutation.mutate(payload);
    } catch (error) {
      toast.error("Unable to encrypt password. Please try again.");
    }
  }

  return (
    <Form {...form}>
      <form
        className={cn("flex flex-col gap-6 sm:gap-8 p-4 sm:p-6 md:p-[30px]", className)}
        {...props}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className="flex flex-col items-center gap-2 sm:gap-3 text-center">
          <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold">
            Neuralpilot Fleet Manager
          </h1>
          <p className="text-muted-foreground text-xs sm:text-sm md:text-[16px] text-balance font-normal px-2 sm:px-0">
            Efficiently track, monitor & optimize your fleet in real time.
          </p>
        </div>
        <div className="grid gap-4 sm:gap-6 md:gap-8">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs sm:text-sm md:text-[16px]">Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    required
                    placeholder="Enter your email"
                    className="h-10 sm:h-11 md:h-[52px] text-sm sm:text-base"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs sm:text-sm md:text-[16px]">
                  Password
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      required
                      placeholder="Enter your password"
                      className="h-10 sm:h-11 md:h-[52px] pr-10 text-sm sm:text-base"
                      {...field}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? (
                        <EyeOff className="size-4 sm:size-5" />
                      ) : (
                        <Eye className="size-4 sm:size-5" />
                      )}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            size="sm"
            type="submit"
            className="w-full h-10 sm:h-11 md:h-[52px] mt-2 sm:mt-3 md:mt-[16px] text-sm sm:text-base"
            disabled={loginMutation.isPending}
          >
            {loginMutation.isPending && (
              <Loader2 className="animate-spin size-4" />
            )}
            Login
          </Button>
        </div>
      </form>
    </Form>
  );
}
